(ns common.widgets.page
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Skeleton$default" :as Skeleton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["react-use/lib/useTitle$default" :as useTitle]
            ["src/svg/inform/lightbulb-coil.svg$default" :as Lightbulb]
            [common.advertiser.hooks :refer [use-demo-advertiser]]
            [common.breadcrumbs.view :refer [breadcrumbs]]
            [common.sidebar.view :as sidebar]
            [common.url-template :as url-tpl]
            [common.util :as util]
            [common.widgets.navigation-bar :refer [navigation-bar]]
            [common.widgets.tab-navigation :refer [tab-navigation]]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(rf/reg-sub-raw ::page-links
  (fn [_]
    (r/reaction
     (let [{:keys [page-links-sub page-links]} @(rf/subscribe [:router/route-data])]
       (if (some? page-links-sub)
         @(rf/subscribe page-links-sub)
         page-links)))))

(def content-padding-sx
  #js {:px 3
       :pb 3})

(def wpp-os-content-padding-sx
  #js {:px "var(--wpp-os-content-padding)"})

(defui demo-advertiser-alert
  []
  (let [url-context (use-subscribe [:url-context])]
    ($ Alert
       {:sx #js {:width "100%"
                 :display "flex"
                 :color "white"
                 :backgroundColor "grey.30"
                 :borderRadius 0
                 ".MuiAlert-action" #js {:flexShrink 0
                                         :pt 0}}
        :severity "default"
        :icon ($ SvgIcon
                 {:sx #js {:width 20
                           :height 20
                           :marginTop 0.2
                           :color "grey.90"}}
                 ($ Lightbulb))
        :action ($ Button
                   {:href (url-tpl/render :docs/article (assoc url-context :slug "24177224749852-Default-Management-for-Content-Variants"))
                    :target "_blank"
                    :sx #js {:color "grey.90"}}
                   "Learn more")}
       "Demo Account (Unverified). Limits apply.")))

(defui page [{:keys [title
                     appbar
                     sidebar
                     sidebar-content
                     sidebar-toggle?
                     sidebar-sx
                     content-sx
                     page-sx
                     sx
                     children]
              :or {appbar true
                   sidebar true
                   sidebar-toggle? true}}] ; defaults to sidebar true
  ;; Title property sets the page title directly.
  ;; Route data :sub-title is appended with app name.
  (let [{:keys [sub-title]} (use-subscribe [:router/route-data])
        page-links (use-subscribe [::page-links])
        {:keys [is-demo]} (use-demo-advertiser)
        os-enabled? (use-subscribe [:common.os/enabled])]
    (useTitle (or title
                  (if (some? sub-title)
                    (str sub-title " | Creative Optimizations")
                    "Creative Optimizations")))
    ($ Stack
       {:direction "column"
        :sx (util/spread-obj #js {:height "100vh"} page-sx)}
       (when appbar
         ($ navigation-bar
            {:sx (when os-enabled?
                   #js {"@media (min-width: 1200px)" wpp-os-content-padding-sx})}))
       (when is-demo
         ($ demo-advertiser-alert))
       ($ Stack
          {:direction "row"
           :flex 1
           :sx (util/spread-obj
                #js {:overflow "hidden"} content-sx)}
          (when sidebar
            ;; Hide the sidebar for now if we don't have any items
            (or (when (some? sidebar-content)
                  ($ sidebar/sidebar
                     {:content sidebar-content
                      :paper-sx sidebar-sx
                      :toggle-button? sidebar-toggle?}))
                (when (some? page-links)
                  ($ sidebar/sidebar
                     {:page-links page-links
                      :paper-sx sidebar-sx
                      :toggle-button? sidebar-toggle?}))))
          ($ Stack
             {:direction "column"
              :flex 1
              :sx (util/spread-obj #js {:alignSelf "initial"
                                        ;; Page should never scroll horizontally.
                                        :overflowX "hidden"} sx)}
             ;; This catches errors inside the page contents, so we can display
             ;; sidebar even when error happened.
             ;; There is another error boundary on router-component
             ;; which handles other errors.
             children)))))

(defui content-padding
  "Adds padding around page content, matching page-header
  paddings."
  [{:keys [children sx] :as props}]
  (let [os-enabled? (use-subscribe [:common.os/enabled])]
    ($ Stack
       (merge {:sx (util/spread-obj #js {:flex 1}
                                    content-padding-sx
                                    (when os-enabled? wpp-os-content-padding-sx)
                                    sx)}
              (dissoc props :sx :children))
       children)))

;; TODO: Consider page-panel which combines content-padding and panel?

(defui page-header
  "Returns hiccup of the page header"
  [{:keys [title buttons tabs sx children]}]
  (let [os-disabled? (use-subscribe [:common.os/disabled])]
    ($ Stack
       {:direction "column"
        :sx (util/spread-obj #js {:px 3
                                  :py 2}
                             (when tabs #js {:pb 0
                                             :borderBottom 1
                                             :borderColor "grey.70"})
                             (when (not os-disabled?) wpp-os-content-padding-sx)
                             sx)}

       ($ breadcrumbs)

       ($ Stack
          {:direction "row"
           :justifyContent "space-between"
           :alignItems "center"
           :gap 2
           :data-testid "page-header-container"
           :sx #js {:mt 1}}
          ($ Typography
             {:variant "h1"
              :noWrap true
              :data-testid "page-header-title"}
             (or title ($ Skeleton {:width "15rem"})))
          (when buttons
            ($ Stack
               {:direction "row"
                :alignItems "center"
                :gap 1
                :data-testid "page-header-actions"}
               buttons)))

       (when children
         ($ Stack
            {:gap 3
             :alignItems "flex-start"
             :sx #js {:mt 1.5}}
            children))

       (when (seq tabs)
         ($ tab-navigation
            {:tabs tabs
             :sx #js {:mt 3}})))))

(defui active-tab [{:keys [tabs sx]}]
  (let [active-tab (use-subscribe [:router/fragment])
        tab (get tabs active-tab)
        tab (or tab
                ;; Old logic was to select the first tab where user has permission.
                ;; Now this and tab-navigation presumes the first tab has the same
                ;; permission as the page itself, and is visible always.
                (first (vals tabs)))
        view (:view tab)]
    ($ content-padding
       {:sx (util/spread-obj
             #js {:pt 3}
             (:container-sx tab)
             sx)}
       ($ view))))
